import endpoints from '@app/src/api/endpoints'
import { FetchKey, useFetchCollectionWithPost, useFetchFacets } from '@app/src/api/fetchHooks'
import { useGetFilterLabels } from '@app/src/hooks/getFilterLabels'
import { useFiltersContext } from '@app/src/pages/ResourceCollection/Filters/Filters'
import type { FilterValue } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { Operators, RESPONSE_ITEM_LATEST_SUBMITTED_FILTER } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import { ONE_MINUTE_MS } from '@app/src/wf-constants'
import type { ChipProps } from '@mui/material'
import { CircularProgress } from '@mui/material'
import React from 'react'
import type { Category } from '../types/categories'
import type { User } from '../types/resourceExplorer'
import StyledChip from './StyledChip'

const staleTime = ONE_MINUTE_MS
const pageSize = 9999

interface FilterDisplayChipProps {
  filterName: string
  filterValue: FilterValue | undefined
  allowDelete?: boolean
  size?: ChipProps['size']
  color?: ChipProps['color']
  operator?: Operators
}

const FilterDisplayChip: React.FC<FilterDisplayChipProps> = ({
  filterName,
  filterValue,
  allowDelete,
  size = 'small',
  color,
  operator,
}) => {
  const { setQueryFilter } = useFiltersContext()

  const isActorTypeIdFilter = filterName.endsWith('actorType.id')
  const isCountryFilter = filterName.endsWith('country.id')
  const isCompanyFilter = filterName.endsWith('target.id')
  const isOrganizationFilter = filterName.endsWith('target.organizationId')
  const isOwnerFilter = filterName.endsWith('providerOwners.userId')
  const isQuestionIdFilter = filterName.endsWith('questionId')
  const isQuestionnaireIdFilter = filterName.endsWith('questionnaireTemplateId')
  const isLegislationIdFilter = filterName.endsWith('template.options.id')
  const isPeriodNameFilter = filterName.endsWith('periodName') && !filterName.endsWith('spends.periodName')
  const isVerificationStatusesFilter = filterName.endsWith('verifications.externalVerificationStatus')
  const isInternalVerificationStatusesFilter = filterName.endsWith('verifications.internalVerificationStatus')
  const isCustomCategoryFilter = filterName.endsWith('categoryOptions.id')
  const isIndustryFilter = filterName.endsWith('industry.id')

  const {
    facets: [countryNames],
    isLoading: isCountryNamesLoading,
  } = useFetchFacets({
    key: FetchKey.CountryFacets,
    endpoint: endpoints.countriesWithFacets,
    facetsParam: [{ name: 'iso3166Alpha2' }],
    options: {
      enabled: isCountryFilter,
    },
    filter: [
      {
        name: 'id',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
    ],
  })

  const {
    facets: [companyNames],
    isLoading: isCompanyNamesLoading,
  } = useFetchFacets({
    key: FetchKey.ProviderFacets,
    endpoint: endpoints.providersWithFacets,
    facetsParam: [{ name: 'name' }],
    options: {
      enabled: isCompanyFilter,
    },
    filter: [
      {
        name: 'id',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
    ],
  })

  const {
    facets: [organizationNames],
    isLoading: isOrganizationNamesLoading,
  } = useFetchFacets({
    key: FetchKey.OrganizationFacets,
    endpoint: endpoints.providersWithFacets,
    facetsParam: [{ name: 'name' }],
    options: {
      enabled: isOrganizationFilter,
    },
    filter: [
      {
        name: 'organization.id',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
    ],
  })

  const { data: userNames, isLoading: isUserNamesLoading } = useFetchCollectionWithPost<User>({
    key: FetchKey.Colleague,
    endpoint: endpoints.colleagues,
    payload: {
      pagination: {
        itemsPerPage: pageSize,
        pageNumber: 1,
      },
      include: [],
      filter: [
        {
          name: 'id',
          filters: [
            {
              value: filterValue,
              operator: Operators.In,
            },
          ],
        },
      ],
    },
    options: {
      enabled: Boolean(isOwnerFilter),
      staleTime: staleTime,
    },
  })

  const {
    facets: [actorTypeNames],
    isLoading: isActorTypeNamesLoading,
  } = useFetchFacets({
    key: FetchKey.ActorTypeFacets,
    endpoint: endpoints.ActorTypesWithFacets,
    facetsParam: [{ name: 'name' }],
    options: {
      enabled: isActorTypeIdFilter,
    },
    filter: [
      {
        name: 'id',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
    ],
  })

  const {
    facets: [questionNames],
    isLoading: isQuestionNamesLoading,
  } = useFetchFacets({
    key: [FetchKey.ResponseItemsFacets, filterName],
    endpoint: endpoints.responseItemsWithFacets,
    facetsParam: [{ name: 'requestItem.template.questionText' }],
    options: {
      enabled: isQuestionIdFilter,
    },
    filter: [
      {
        name: 'requestItem.questionId',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
      RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
    ],
  })

  const {
    facets: [questionnaireNames],
    isLoading: isQuestionnaireNamesLoading,
  } = useFetchFacets({
    key: FetchKey.RequestFacets,
    endpoint: endpoints.requestsWithFacets,
    facetsParam: [{ name: 'template.title' }],
    options: {
      enabled: isQuestionnaireIdFilter,
    },
    filter: [
      {
        name: 'template.id',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
    ],
  })

  const {
    facets: [legislationNames],
    isLoading: isLegislationNamesLoading,
  } = useFetchFacets({
    key: [FetchKey.ResponseItemsFacets, filterName],
    endpoint: endpoints.responseItemsWithFacets,
    facetsParam: [{ name: 'response.request.template.sections.questions.options.name' }],
    options: {
      enabled: isLegislationIdFilter,
    },
    filter: [
      {
        name: 'response.request.template.sections.questions.options.id',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
      {
        name: 'response.request.template.sections.questions.options.category.name',
        filters: [{ value: 'Legislation', operator: Operators.EqualTo }],
      },
      RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
    ],
  })

  const {
    facets: [periodNames],
    isLoading: isPeriodNamesLoading,
  } = useFetchFacets({
    key: [FetchKey.ResponseItemsFacets, filterName],
    endpoint: endpoints.responseItemsWithFacets,
    facetsParam: [{ name: 'response.request.periodName', isEnum: true }],
    options: {
      enabled: isPeriodNameFilter,
    },
    filter: [
      {
        name: 'response.request.periodName',
        filters: [
          {
            value: Array.isArray(filterValue) ? filterValue : [String(filterValue)],
            operator: Operators.In,
          },
        ],
      },
      RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
    ],
  })

  const {
    facets: [verificationStatuses],
    isLoading: isVerificationStatusesLoading,
  } = useFetchFacets({
    key: [FetchKey.ResponseItemsFacets, filterName],
    endpoint: endpoints.responseItemsWithFacets,
    facetsParam: [{ name: filterName, isEnum: true }],
    options: {
      enabled: isVerificationStatusesFilter,
    },
    filter: [
      {
        name: 'response.verifications.externalVerificationStatus',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
      RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
    ],
  })

  const {
    facets: [internalVerificationStatuses],
    isLoading: isInternalVerificationStatusesLoading,
  } = useFetchFacets({
    key: [FetchKey.ResponseItemsFacets, filterName],
    endpoint: endpoints.responseItemsWithFacets,
    facetsParam: [{ name: filterName, isEnum: true }],
    options: {
      enabled: isInternalVerificationStatusesFilter,
    },
    filter: [
      {
        name: 'response.verifications.internalVerificationStatus',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
      RESPONSE_ITEM_LATEST_SUBMITTED_FILTER,
    ],
  })

  const { data: categories } = useFetchCollectionWithPost<Category>({
    key: FetchKey.Category,
    endpoint: endpoints.categoryGetWithIds,
    payload: {
      filter: [
        {
          name: 'categoryOptions.id',
          filters: [
            {
              value: filterValue,
              operator: Operators.In,
            },
          ],
        },
      ],
      include: [],
    },
    options: {
      enabled: Boolean(isCustomCategoryFilter),
      staleTime: staleTime,
    },
  })

  const {
    facets: [industries],
    isLoading: isIndustriesLoading,
  } = useFetchFacets({
    key: FetchKey.Category,
    endpoint: endpoints.naceCodesWithFacets,
    facetsParam: [{ name: 'description' }],
    filter: [
      {
        name: 'id',
        filters: [
          {
            value: filterValue,
            operator: Operators.In,
          },
        ],
      },
    ],
    options: {
      enabled: Boolean(isIndustryFilter),
      staleTime: staleTime,
    },
  })

  const isLoading =
    isCountryNamesLoading ||
    isCompanyNamesLoading ||
    isUserNamesLoading ||
    isActorTypeNamesLoading ||
    isOrganizationNamesLoading ||
    isQuestionNamesLoading ||
    isQuestionnaireNamesLoading ||
    isLegislationNamesLoading ||
    isPeriodNamesLoading ||
    isVerificationStatusesLoading ||
    isInternalVerificationStatusesLoading ||
    isIndustriesLoading

  const { getFilterLabel } = useGetFilterLabels({
    countryNames,
    companyNames,
    userNames:
      userNames?.items?.map(user => ({
        value: user.id,
        label: user.name,
        count: 1,
      })) ?? [],
    categoryNames:
      categories?.items?.flatMap(cat => (cat.id ? [{ value: cat.id, label: cat.name, count: 1 }] : [])) ?? [],
    actorTypeNames,
    organizationNames,
    questionNames,
    questionnaireNames,
    legislationNames,
    periodNames,
    verificationStatuses,
    internalVerificationStatuses,
    industries,
  })

  return (
    <StyledChip
      label={isLoading ? <CircularProgress size={12} /> : getFilterLabel(filterName, filterValue, operator)}
      size={size}
      onDelete={allowDelete ? () => setQueryFilter(filterName, '', Operators.In) : undefined}
      color={color}
    />
  )
}

export default FilterDisplayChip
